import { useEffect, useState } from 'react';
import { App, Form, Input, InputNumber, Modal, Select, Switch, Typography } from 'antd';
import { useEnterprises } from '@/hooks/useEnterprises';
import { useProfiles } from '@/hooks/useProfiles';
import { useCreateEnrollmentToken } from '@/hooks/useEnrollment';
import type { EnrollmentTokenDto } from '@/types/enrollment';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (token: EnrollmentTokenDto) => void;
}

interface FormValues {
  enterpriseId: number;
  profileId: number;
  durationDays: number;
  oneTimeOnly: boolean;
  label?: string;
}

export function CreateEnrollmentTokenModal({ open, onClose, onCreated }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState<number | undefined>();

  const { data: enterprises } = useEnterprises({ pageSize: 100 });
  // El select de Profile se filtra por la Enterprise seleccionada en el form
  const { data: profiles, isLoading: profilesLoading } = useProfiles({
    pageSize: 100,
    enterpriseId: selectedEnterpriseId,
  });

  const createMut = useCreateEnrollmentToken();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ durationDays: 7, oneTimeOnly: false });
      setSelectedEnterpriseId(undefined);
    }
  }, [open, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      const result = await createMut.mutateAsync({
        enterpriseId: values.enterpriseId,
        profileId: values.profileId,
        durationDays: values.durationDays,
        oneTimeOnly: values.oneTimeOnly,
        label: values.label?.trim() || undefined,
      });

      message.success('Token de enrolamiento creado.');
      onCreated(result);
    } catch (err: any) {
      if (err?.errorFields) return;
    }
  };

  const handleEnterpriseChange = (eid: number) => {
    setSelectedEnterpriseId(eid);
    // Limpiar el profile cuando cambia la enterprise
    form.setFieldsValue({ profileId: undefined as any });
  };

  return (
    <Modal
      title="Generar token de enrolamiento"
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText="Generar QR"
      cancelText="Cancelar"
      confirmLoading={createMut.isPending}
      destroyOnClose
      width={560}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Al generar un token, el dispositivo Android nuevo (o reseteado a fabrica) podra
        enrolarse escaneando el QR durante el setup wizard. El device quedara con la
        policy del perfil indicado.
      </Text>

      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item
          label="Enterprise"
          name="enterpriseId"
          rules={[{ required: true, message: 'Enterprise requerida.' }]}
        >
          <Select
            placeholder="Selecciona una Enterprise"
            options={(enterprises?.items ?? []).map((e) => ({ value: e.id, label: e.displayNameEs }))}
            onChange={handleEnterpriseChange}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          label="Perfil (Policy)"
          name="profileId"
          rules={[{ required: true, message: 'Perfil requerido.' }]}
          extra="El device quedara con esta policy aplicada al enrolarse."
        >
          <Select
            placeholder={selectedEnterpriseId ? 'Selecciona un perfil' : 'Primero selecciona una Enterprise'}
            disabled={!selectedEnterpriseId}
            loading={profilesLoading}
            options={(profiles?.items ?? [])
              .filter((p) => p.isActive)
              .map((p) => ({ value: p.id, label: `${p.displayNameEs} (${p.name})` }))}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          label="Duracion (dias)"
          name="durationDays"
          rules={[
            { required: true, message: 'Duracion requerida.' },
            { type: 'number', min: 1, max: 90, message: 'Entre 1 y 90 dias.' },
          ]}
          extra="Maximo 90 dias (limite de Google AMAPI)."
        >
          <InputNumber min={1} max={90} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Un solo uso"
          name="oneTimeOnly"
          valuePropName="checked"
          extra="Si esta activo, el QR solo sirve para enrolar UN dispositivo. Si esta inactivo, se puede usar para enrolar varios devices con el mismo perfil."
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Etiqueta (opcional)"
          name="label"
          extra="Para identificar el lote (ej: 'Tanda Mayo 2026 - Instalaciones')."
        >
          <Input maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
